import { createContext, useContext, useState, useCallback } from 'react';
import QuoteModal from '@/components/QuoteModal';

const QuoteModalContext = createContext(null);

export const useQuoteModal = () => {
  const context = useContext(QuoteModalContext);
  if (!context) {
    throw new Error('useQuoteModal must be used within a QuoteModalProvider');
  }
  return context;
};

export const QuoteModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [preselectedCategory, setPreselectedCategory] = useState(null);
  const [sourcePage, setSourcePage] = useState(null);
  const [contextHint, setContextHint] = useState(null);

  // Open modal with optional category preselection
  const openModal = useCallback((category = null, source = null) => {
    setPreselectedCategory(category);
    setSourcePage(source || window.location.pathname);
    setContextHint(null);
    setIsOpen(true);
  }, []);

  // Open modal directly at auto step
  const openAutoModal = useCallback((source = null) => {
    setPreselectedCategory('auto');
    setSourcePage(source || window.location.pathname);
    setContextHint(null);
    setIsOpen(true);
  }, []);

  // Open modal directly at home step
  const openHomeModal = useCallback((source = null) => {
    setPreselectedCategory('home');
    setSourcePage(source || window.location.pathname);
    setContextHint(null);
    setIsOpen(true);
  }, []);

  // Open modal directly at business step
  const openBusinessModal = useCallback((source = null, hint = null) => {
    setPreselectedCategory('business');
    setSourcePage(source || window.location.pathname);
    setContextHint(hint);
    setIsOpen(true);
  }, []);

  // Open modal directly at life step
  const openLifeModal = useCallback((source = null) => {
    setPreselectedCategory('life');
    setSourcePage(source || window.location.pathname);
    setContextHint(null);
    setIsOpen(true);
  }, []);

  // Open modal directly at travel step (Talk-to-Us)
  const openTravelModal = useCallback((source = null) => {
    setPreselectedCategory('travel');
    setSourcePage(source || window.location.pathname);
    setContextHint(null);
    setIsOpen(true);
  }, []);

  // Open modal directly at marine step
  const openMarineModal = useCallback((source = null) => {
    setPreselectedCategory('marine');
    setSourcePage(source || window.location.pathname);
    setContextHint(null);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setPreselectedCategory(null);
    setSourcePage(null);
    setContextHint(null);
  }, []);

  return (
    <QuoteModalContext.Provider value={{ 
      openModal, 
      openAutoModal,
      openHomeModal,
      openBusinessModal, 
      openLifeModal,
      openTravelModal,
      openMarineModal,
      closeModal, 
      isOpen 
    }}>
      {children}
      <QuoteModal 
        open={isOpen} 
        onOpenChange={setIsOpen}
        preselectedCategory={preselectedCategory}
        sourcePage={sourcePage}
        contextHint={contextHint}
      />
    </QuoteModalContext.Provider>
  );
};

export default QuoteModalProvider;
